import { useCallback } from 'react'
import { useLatest } from 'react-use'
import { multiEditConfig } from '@editorUtils'
import { filter, some } from 'lodash-es'
import { coordinateEventName } from '@ui/events'
import { useControlStore, useEditElementStore } from '@editorStores'
import {
  useGuidToElementIncludingRips,
  useIsKeyPressed,
  useMultiSelectionEvent,
  useIfcImportSelectionActive,
} from '@editorHooks'
import { pointerModifiers } from 'src/shortcuts/pointerModifiers'

/**
 * This hook should be used for all scene elements in the floorplan view in
 * order to enhance the click event passed to the element with 'global' click
 * events (e.g. for hiding). It is not ideal to make low level elements like a
 * wall interact with the global control store, but I think this is the best way
 * to implement uniform behaviour accross the whole app.
 * @param elementGuid
 * @param onClick The onClick function to execute if no global function is
 * enabled
 */
const useEnhanceClickEvent = (onClick?: (elementGuid: string, ...args: any[]) => unknown) => {
  const multiSelection = useMultiSelectionEvent()
  const { isPressed: isMultiSelection } = useIsKeyPressed(pointerModifiers.multiSelect)

  const selectionModeKey = useControlStore(state => state.selectionModeKey)
  const ifcDrawer = useIfcImportSelectionActive()

  // Check if coordinate input mode is active
  const isCoordinateInputMode = selectionModeKey?.startsWith(coordinateEventName)

  const multiActiveElements = useEditElementStore(state => state.multiActiveElements)
  const setMultiActiveElements = useEditElementStore(state => state.setMultiActiveElements)

  const activeElement = useEditElementStore(state => state.activeElement)
  const setActiveElement = useEditElementStore(state => state.setActiveElement)

  const guidToElement = useGuidToElementIncludingRips()

  const shouldBeAdded = useCallback(
    (elementGuid: string, selectedElementGuids: string[]) => {
      const elements = [elementGuid, ...selectedElementGuids].map(guid => guidToElement[guid])

      // necessary to prevent adding elements that are not in the model (e.g. stiffening intervals)
      if (some(elements, el => !el)) return false

      const config = multiEditConfig(elements)

      return some(Object.values(config))
    },
    [guidToElement],
  )

  const addMultiSelect = useCallback(
    (element: string) => {
      setMultiActiveElements(
        activeElement ? [activeElement, element] : [...multiActiveElements, element],
      )
      setActiveElement(null)
    },
    [activeElement, multiActiveElements, setActiveElement, setMultiActiveElements],
  )

  const removeMultiSelect = useCallback(
    (element: string) => {
      setMultiActiveElements(filter(multiActiveElements, guid => guid !== element))
      setActiveElement(null)
    },
    [multiActiveElements, setActiveElement, setMultiActiveElements],
  )

  const isClickable = !!(isMultiSelection || onClick) && !isCoordinateInputMode && !ifcDrawer

  const onClickEnhanced = useCallback(
    (elementGuid: string, ...args: any[]) => {
      // Do nothing because the ifc drawer is active
      if (ifcDrawer) return

      if (
        multiSelection(
          elementGuid,
          activeElement ? [activeElement, ...multiActiveElements] : multiActiveElements,
          addMultiSelect,
          removeMultiSelect,
          shouldBeAdded,
        )
      ) {
        // DO NOTHING because it has already been handled by the multiSelection hook
      } else {
        onClick?.(elementGuid, ...args)
        if (multiActiveElements.length > 0) {
          setMultiActiveElements([])
        }
      }
    },
    [
      ifcDrawer,
      multiSelection,
      activeElement,
      multiActiveElements,
      addMultiSelect,
      removeMultiSelect,
      shouldBeAdded,
      onClick,
      setMultiActiveElements,
    ],
  )

  const onClickBase = useCallback(
    (elementGuid: string) => {
      // Do nothing because the ifc drawer is active
      if (ifcDrawer) return

      const selectionHandled = multiSelection(
        elementGuid,
        activeElement ? [activeElement, ...multiActiveElements] : multiActiveElements,
        addMultiSelect,
        removeMultiSelect,
        shouldBeAdded,
      )

      if (!selectionHandled && multiActiveElements.length > 0) setMultiActiveElements([])
    },
    [
      ifcDrawer,
      multiSelection,
      activeElement,
      multiActiveElements,
      addMultiSelect,
      removeMultiSelect,
      shouldBeAdded,
      setMultiActiveElements,
    ],
  )

  const onClickBaseRef = useLatest(onClickBase)
  const onClickEnhancedRef = useLatest(onClickEnhanced)

  return {
    onClickEnhancedRef,
    onClickBaseRef,
    isClickable,
  }
}

export default useEnhanceClickEvent
